import type { LangCode } from "@/api/types";

import { EditableSection } from "./EditableSection";
import { Field } from "./Field";
import type { RecordTabFormProps } from "./shared";

/** Форма для item_type=glossary_term — пара EN/RU + readonly synonyms /
 *  evidence / sources. Никакого pickFirst — поля жёстко привязаны
 *  к keys payload'а. */
export function GlossaryTermForm({
  item,
  payload,
  editMode,
  draft,
  onChange,
  onCancelEdit,
}: RecordTabFormProps) {
  const srcLang = (item.source_lang ?? null) as LangCode | null;
  const tgtLang = (item.target_lang ?? null) as LangCode | null;

  const enValue =
    editMode && draft.en_term != null
      ? draft.en_term
      : (payload.en_term ?? payload.suggested_en_term ?? item.source_text ?? "");
  const ruValue =
    editMode && draft.ru_term != null
      ? draft.ru_term
      : (payload.ru_term ?? item.candidate_text ?? "");

  return (
    <>
      <EditableSection
        title="Терминологическая пара"
        editMode={editMode}
        draftCount={Object.keys(draft).length}
        onCancelEdit={onCancelEdit}
      >
        <div className="rf-form-grid">
          <Field
            fieldKey="en_term"
            label="EN"
            langTag={srcLang}
            value={enValue}
            editable={editMode}
            keepEmpty
            onChange={onChange}
          />
          <Field
            fieldKey="ru_term"
            label="RU"
            langTag={tgtLang}
            value={ruValue}
            editable={editMode}
            keepEmpty
            onChange={onChange}
          />
        </div>
      </EditableSection>

      <ContextSection payload={payload} item={item} />
    </>
  );
}

function ContextSection({
  payload,
  item,
}: {
  payload: RecordTabFormProps["payload"];
  item: RecordTabFormProps["item"];
}) {
  const synonyms = (payload.synonyms ?? [])
    .map((s) => (typeof s === "string" ? s : ((s as { text?: string }).text ?? "")))
    .filter(Boolean) as string[];
  const reviewReason = payload.review_reason;
  const evidenceCount = payload.evidence_count;
  const sources = payload.active_sources ?? [];
  const externalIds = payload.external_ids;

  if (
    synonyms.length === 0 &&
    !reviewReason &&
    evidenceCount == null &&
    sources.length === 0 &&
    !externalIds &&
    !item.source_key
  ) {
    return null;
  }

  return (
    <section className="rf-section">
      <div className="rf-section__head">
        <span>Контекст</span>
      </div>
      <div className="rf-section__body">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(160px, max-content) 1fr",
            gap: "4px 12px",
            fontSize: "var(--fs-sm)",
          }}
        >
          {reviewReason && <Row k="Причина" v={reviewReason} />}
          {evidenceCount != null && <Row k="Evidence" v={evidenceCount} />}
          {sources.length > 0 && <Row k="Источники" v={sources.join(", ")} />}
          {synonyms.length > 0 && <Row k="Синонимы" v={synonyms.join(" · ")} />}
          {item.source_key && <Row k="source_key" v={item.source_key} />}
        </div>
      </div>
    </section>
  );
}

function Row({ k, v }: { k: string; v: unknown }) {
  return (
    <>
      <div className="mt-mono mt-text-tight mt-muted">{k}</div>
      <div style={{ overflowWrap: "anywhere" }}>{String(v)}</div>
    </>
  );
}
